import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Checkbox from "@mui/material/Checkbox";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { Lightbox } from "react-modal-image";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import { toast } from "react-toastify";

import CloseIcon from "@mui/icons-material/Close";
import DiscardChangesConfirm from "./DiscardChangesConfirm";
import AddIcon from "@mui/icons-material/Add";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import ImageIcon from "@mui/icons-material/Image";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import BrokenImageOutlinedIcon from "@mui/icons-material/BrokenImageOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import TuneIcon from "@mui/icons-material/Tune";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import DesktopWindowsOutlinedIcon from "@mui/icons-material/DesktopWindowsOutlined";
import SmartphoneOutlinedIcon from "@mui/icons-material/SmartphoneOutlined";
import QuizIcon from "@mui/icons-material/Quiz";

import {
  API_generate_quiz_questions_with_ai,
  API_post_file_upload_from_learning_file,
  API_get_file_upload,
  API_post_file_upload_with_eagerly_generated_presigned_url,
} from "../../../constants";
import {
  getDefaultAxiosConfig,
  getRightworkAPI,
  postRightworkAPI,
} from "../requests/utilities/requests";
import { streamQuizGenerationAPI } from "../requests/utilities/streamingRequests";
import LoadSpinner from "../../../utilities/LoadSpinner";
import CustomCategorySelect from "./CustomCategorySelect";
import AiQuizGeneratorModal from "./AiQuizGeneratorModal";
import MediaLibraryModal from "./MediaLibraryModal";
import CollectionsOutlinedIcon from "@mui/icons-material/CollectionsOutlined";
import CompleteQuizModal from "./CompleteQuizModal";
import DeleteWarningModal from "./DeleteWarningModal";
import ReuseSignalBanner from "./ReuseSignalBanner";
import DirectAssignabilityToggle, {
  setDirectAssignability,
} from "./DirectAssignabilityToggle";
import SettingToggleCard from "./SettingToggleCard";

// = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = =
// Constants & factories

const modalStyle = {
  // Center without `transform` — @hello-pangea/dnd's drag clone uses
  // `position: fixed`, which becomes relative to the nearest transformed
  // ancestor instead of the viewport. With a transformed modal wrapping a
  // scrollable drag list, dragging offsets the ghost by the scroll amount.
  // `inset: 0 + margin: auto` centers identically without a transform.
  position: "absolute",
  inset: 0,
  margin: "auto",
  width: "96%",
  height: "94%",
  maxWidth: "1600px",
  maxHeight: "94vh",
  bgcolor: "background.paper",
  borderRadius: "12px",
  boxShadow: 24,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

// Cap at 8 — past that, learners struggle to read the choices and
// authoring intent gets fuzzy. Mirrors common assessment-tool defaults
// (Google Forms allows more, but most published quizzes stay <= 6).
const MAX_ANSWERS = 8;
const DEFAULT_NEW_ANSWER_COUNT = 4;

const ANSWER_LETTERS = [
  "A", "B", "C", "D", "E", "F", "G", "H",
];

const makeDefaultAnswer = () => ["", false, null, null];

const makeDefaultQuestion = () => ({
  answers: Array.from({ length: DEFAULT_NEW_ANSWER_COUNT }, makeDefaultAnswer),
  created_at: null,
  question: "",
  question_v2: ["", null, null],
  updated_at: null,
  uuid: null,
  is_drag_and_drop: false,
});

// = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = =
// Helpers

const countCorrectAnswers = (question) =>
  (question?.answers || []).filter(([, isCorrect]) => isCorrect).length;

const questionHasImage = (question) => Boolean(question?.question_v2?.[1]);

const getQuestionTitle = (question, fallbackIndex) => {
  const text = question?.question_v2?.[0] || question?.question || "";
  return text.trim() || `Question ${fallbackIndex + 1}`;
};

// Upload a file to S3 via a presigned URL. Returns the file UUID; the
// presigned URL itself is short-lived and isn't returned — callers fetch
// a fresh one on demand via API_get_file_upload when they need to
// display the image.
const uploadFile = async (file, type, companyId) => {
  const formData = new FormData();
  formData.append("company_id", companyId);
  formData.append("file_name", file.name);
  formData.append("type", type);

  const axiosConfig = await getDefaultAxiosConfig();
  const res = await axios.post(
    API_post_file_upload_with_eagerly_generated_presigned_url,
    formData,
    {
      headers: {
        ...axiosConfig.headers,
        "Content-Type": "multipart/form-data",
      },
    },
  );
  const fileUUID = res?.data?.redux?.payload?.uuid;
  const presignedUrl = res?.data?.redux?.payload?.presigned_url;

  await axios.put(presignedUrl, file, {
    headers: { "Content-Type": file.type },
  });

  return fileUUID;
};

// = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = =
// Main component

/**
 * Three-pane quiz editor.
 *
 *   Left:   question list, drag-to-reorder, +Add menu (new / AI generate)
 *   Center: selected question editor (text, image, dynamic answers, D&D)
 *   Right:  quiz-level settings (category, pass %, retakes, show answers)
 *
 * Header carries the quiz name (inline TextField) plus Preview and Save.
 * Cancel-revert via originalQuizRef snapshot taken on open. Save calls
 * the parent's post_learning_quiz; the parent owns modalDataQuiz state
 * so the AI generator can append into it without prop-drilling.
 */
function QuizEditorModal(props) {
  const {
    open,
    handleClose: parentHandleClose,
    modalEdit,
    // True while the parent's fetchQuizDetail is in flight (edit path
    // only). Critical: the parent opens this modal BEFORE the fetch
    // resolves, so modalDataQuiz can still hold the previous quiz (or
    // a default) until the load completes. Save and the editor body
    // both gate on this so a quick click can't post stale data.
    isLoading,
    // True when the parent's quiz-detail load failed (deleted quiz, permission
    // error, transient failure). modalDataQuiz then holds the previous quiz, so
    // the body + Save are blocked to stop a Save overwriting the wrong quiz.
    loadError,
    modalDataQuiz,
    setModalDataQuiz,
    post_learning_quiz,
    delete_learning_quiz,
    homeCompany,
    quizzesData,
    sectionsData,
    modulesData,
    onSaveSuccess,
  } = props;

  const isEdit = Boolean(modalEdit);

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // State

  const [showSettings, setShowSettings] = useState(!isEdit);
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(null);
  const [hoveredQuestionIndex, setHoveredQuestionIndex] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [openAiModal, setOpenAiModal] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);
  const [previewViewport, setPreviewViewport] = useState("desktop");
  const [previewMenuAnchor, setPreviewMenuAnchor] = useState(null);
  const [openDeleteWarning, setOpenDeleteWarning] = useState(false);
  // "Close without saving?" guard (the X sits next to Save).
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);

  // Live status of an in-flight AI generation: { current, total } while
  // streaming, null when idle. Drives the "Generating X of N…" chip in
  // the rail header so the user sees questions populate in real time.
  const [aiGenStatus, setAiGenStatus] = useState(null);

  const [showLightbox, setShowLightbox] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);

  // Toggles for the right-pane numeric settings. Stored locally so an
  // unchecked box can null out the underlying value while still
  // remembering the previous number when the user re-checks.
  const [passPercentageEnabled, setPassPercentageEnabled] = useState(false);
  const [retakeLimitEnabled, setRetakeLimitEnabled] = useState(false);

  const originalQuizRef = useRef(null);

  // AbortController for the in-flight AI generation stream. Aborted on
  // handleClose so late SSE events can't keep mutating modalDataQuiz
  // after the user dismisses the editor — without this, closing to
  // "discard" leaves stragglers that pollute the parent's modalDataQuiz
  // and bleed into the next quiz the user opens.
  const aiAbortRef = useRef(null);

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Effects

  useEffect(() => {
    if (!open) return;
    originalQuizRef.current = modalDataQuiz;
    setShowSettings(!isEdit);
    setSelectedQuestionIndex(
      (modalDataQuiz?.learning_questions?.length || 0) > 0 ? 0 : null,
    );
    setPassPercentageEnabled(
      modalDataQuiz?.pass_percentage !== null &&
        modalDataQuiz?.pass_percentage !== undefined,
    );
    setRetakeLimitEnabled(
      modalDataQuiz?.retake_limit !== null &&
        modalDataQuiz?.retake_limit !== undefined,
    );
    // Re-fires when the loaded quiz's UUID changes, not just on open.
    // QuizzesDisplay opens the modal BEFORE fetchQuizDetail resolves,
    // so the first run of this effect snapshots stale modalDataQuiz
    // (last quiz, or the default blank). When the real payload lands,
    // its UUID differs and we re-derive the toggle/snapshot state from
    // it. Safe against clobbering mid-session edits because the body
    // is gated on isLoading until the fetch resolves — by the time the
    // user can interact, this effect has already settled on real data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, modalDataQuiz?.uuid]);

  // Re-snapshot when the detail fetch settles. Reopening a quiz that was just
  // saved keeps its UUID (so the effect above doesn't re-run) while the
  // fetched questions now carry server UUIDs; without this the close guard
  // would see a spurious diff and offer to discard nothing.
  useEffect(() => {
    if (!open || isLoading || loadError) return;
    originalQuizRef.current = modalDataQuiz;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isLoading, loadError]);

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Derived

  const questions = modalDataQuiz?.learning_questions || [];
  const selectedQuestion =
    selectedQuestionIndex !== null ? questions[selectedQuestionIndex] : null;

  const distinctCategories = useMemo(() => {
    if (!quizzesData || quizzesData.length === 0) return [];
    return [...new Set(quizzesData.map((q) => q.category))].filter(Boolean);
  }, [quizzesData]);

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Quiz-level handlers

  const updateQuiz = (patch) => {
    setModalDataQuiz((prev) => ({ ...prev, ...patch }));
  };

  const handleNameChange = (e) => updateQuiz({ name: e.target.value });

  const handleCategoryChange = (option) => updateQuiz({ category: option });

  const handleTestOutChange = (e) => {
    const isTestOut = e.target.checked;
    if (!isTestOut) {
      updateQuiz({ gate_on_pass: true });
      return;
    }
    // One attempt with answers hidden, so a retry can't just be memorized.
    setPassPercentageEnabled(true);
    setRetakeLimitEnabled(true);
    updateQuiz({
      gate_on_pass: false,
      show_correct_answers: false,
      retake_limit: 1,
      pass_percentage: modalDataQuiz?.pass_percentage || "",
    });
  };

  const handlePassPercentageToggle = (e) => {
    const checked = e.target.checked;
    // A test-out quiz needs both a pass mark and an attempt limit.
    if (!checked && modalDataQuiz?.gate_on_pass === false) {
      toast.info("A test-out quiz needs a passing percentage.");
      return;
    }
    setPassPercentageEnabled(checked);
    if (!checked) {
      // Disabling pass % also disables retake limit — the latter only
      // makes sense when there's a passing threshold to gate against.
      updateQuiz({ pass_percentage: null, retake_limit: null });
      setRetakeLimitEnabled(false);
    } else if (modalDataQuiz?.pass_percentage == null) {
      updateQuiz({ pass_percentage: "" });
    }
  };

  const handleRetakeLimitToggle = (e) => {
    const checked = e.target.checked;
    if (!checked && modalDataQuiz?.gate_on_pass === false) {
      toast.info("A test-out quiz needs an attempt limit.");
      return;
    }
    setRetakeLimitEnabled(checked);
    if (!checked) {
      updateQuiz({ retake_limit: null });
    } else if (modalDataQuiz?.retake_limit == null) {
      updateQuiz({ retake_limit: "" });
    }
  };

  const handleShowCorrectAnswersChange = (e) =>
    updateQuiz({ show_correct_answers: e.target.checked });

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Question list mutations

  const updateQuestionAt = (index, updater) => {
    setModalDataQuiz((prev) => {
      const next = [...(prev.learning_questions || [])];
      // The question can vanish between capture and apply (e.g. removed
      // while an async library-copy was in flight). Writing past the
      // end would create a phantom/undefined question row.
      if (index == null || index < 0 || index >= next.length) return prev;
      let updated =
        typeof updater === "function" ? updater(next[index]) : updater;
      // Editing counts as keeping: ANY mutation of an AI-generated
      // question clears its pending-review flag, so a later "Discard"
      // can't delete work the author has invested in. This is the
      // single chokepoint every question/answer edit flows through
      // (updateAnswerAt -> updateSelectedQuestion -> here), so the
      // rule can't be missed by a future edit handler.
      if (updated && updated._aiPending) {
        const { _aiPending, ...rest } = updated;
        updated = rest;
      }
      next[index] = updated;
      return { ...prev, learning_questions: next };
    });
  };

  const handleAddQuestion = () => {
    setModalDataQuiz((prev) => {
      const next = [...(prev.learning_questions || []), makeDefaultQuestion()];
      Promise.resolve().then(() => setSelectedQuestionIndex(next.length - 1));
      return { ...prev, learning_questions: next };
    });
    setAddMenuAnchor(null);
  };

  const handleOpenAiModal = () => {
    setAddMenuAnchor(null);
    setOpenAiModal(true);
  };

  // Streaming AI question generation. The AiQuizGeneratorModal awaits
  // this function and closes itself when it resolves — we resolve
  // synchronously after kicking the stream off (no `await` on the
  // streaming call) so the modal closes immediately and the user
  // watches questions populate in the rail. Progress lives on
  // aiGenStatus → renders as a chip in QuestionListPane's header.
  //
  // streamQuizGenerationAPI both invokes onError AND throws on `error`
  // SSE events; an `errorHandled` latch keeps the toast from firing
  // twice. Network/HTTP failures surface via the catch instead.
  const handleAiGenerate = async ({
    sectionUuids = [],
    moduleUuids = [],
    fileUuids = [],
    pastedText = "",
    numQuestions,
    customContext = "",
    difficulty = "mixed",
  } = {}) => {
    // Abort any prior in-flight generation before starting a new one —
    // protects against the user re-firing the AI modal while a previous
    // stream is still running and would race the new one's events.
    if (aiAbortRef.current) {
      aiAbortRef.current.abort();
    }
    const controller = new AbortController();
    aiAbortRef.current = controller;

    setAiGenStatus({ current: 0, total: numQuestions });
    let errorHandled = false;

    streamQuizGenerationAPI(
      API_generate_quiz_questions_with_ai,
      {
        company_id: homeCompany,
        learning_section_uuids: JSON.stringify(sectionUuids),
        learning_module_uuids: JSON.stringify(moduleUuids),
        file_upload_uuids: JSON.stringify(fileUuids),
        pasted_text: pastedText,
        num_questions: numQuestions,
        custom_context: customContext,
        difficulty: difficulty,
      },
      {
        onStarted: (total) => {
          if (aiAbortRef.current !== controller) return;
          setAiGenStatus({ current: 0, total: total ?? numQuestions });
        },
        onWarning: (text) => {
          if (aiAbortRef.current !== controller) return;
          // Context-scope notice from the backend (document still
          // indexing, retrieval fallback, truncation) — inform without
          // interrupting the stream.
          toast.warning(text, { autoClose: 6000 });
        },
        onQuestion: (question) => {
          // Drop events from a stream that's no longer the current run —
          // covers both the abort path (ref nulled) and the supersede
          // path (ref reassigned to a newer controller). Either way,
          // mutating state would corrupt the active session.
          if (aiAbortRef.current !== controller) return;
          setModalDataQuiz((prev) => ({
            ...prev,
            learning_questions: [
              ...(prev.learning_questions || []),
              // Transient review flag: drives the AI badge + the
              // Keep/Discard bar. Never persisted — the save endpoint
              // reads only known fields, and a saved quiz reloads
              // clean from the backend.
              { ...question, _aiPending: true },
            ],
          }));
          setAiGenStatus((prev) =>
            prev ? { ...prev, current: prev.current + 1 } : null,
          );
        },
        onDone: (count) => {
          if (aiAbortRef.current !== controller) return;
          setAiGenStatus(null);
          toast.success(
            `Generated ${count} ${count === 1 ? "question" : "questions"} — review them in the list.`,
            { autoClose: 3000 },
          );
        },
        onError: (msg) => {
          if (aiAbortRef.current !== controller) return;
          errorHandled = true;
          setAiGenStatus(null);
          toast.error(msg, { autoClose: 3000 });
        },
      },
      { signal: controller.signal },
    ).catch((err) => {
      // If a newer generation has taken over (or handleClose nulled
      // the ref), this older stream's settle path must NOT touch
      // aiGenStatus — doing so would hide progress for the run that's
      // actually active and make the new chip blink in/out.
      if (aiAbortRef.current !== controller) return;

      // Aborted by handleClose (or a follow-up generation) — clear the
      // chip silently. No toast; this is the user's intent, not a fault.
      if (controller.signal.aborted || err?.name === "AbortError") {
        setAiGenStatus(null);
        return;
      }
      setAiGenStatus(null);
      if (!errorHandled) {
        console.error("AI quiz generation failed", err);
        // Prefer the helper-propagated message (server's
        // system.message envelope on 4xx, or the SSE error event's
        // text). Fall back only when err has no useful message —
        // e.g. a fetch-level network failure with a stack-only Error.
        toast.error(
          err?.message || "Failed to generate questions. Please try again.",
          { autoClose: 3000 },
        );
      }
    });
    // Intentionally NOT awaited above — return immediately so the AI
    // modal closes and the user sees streamed progress in the rail.
  };

  const handleRemoveQuestion = (index) => {
    setModalDataQuiz((prev) => {
      const next = (prev.learning_questions || []).filter((_, i) => i !== index);
      return { ...prev, learning_questions: next };
    });
    setSelectedQuestionIndex((curr) => {
      if (curr === null) return curr;
      if (curr === index) return null;
      if (curr > index) return curr - 1;
      return curr;
    });
  };

  const handleQuestionDragEnd = (result) => {
    if (!result.destination) return;
    const { source, destination } = result;
    if (source.index === destination.index) return;

    setModalDataQuiz((prev) => {
      const next = Array.from(prev.learning_questions || []);
      const [moved] = next.splice(source.index, 1);
      next.splice(destination.index, 0, moved);
      return { ...prev, learning_questions: next };
    });
    // Keep selection pointing at the same logical question after the
    // reorder. Same fix as SwipeDeckInputsModal — without this the
    // editor pane silently swaps to a different question whenever a
    // non-selected question is dragged past the selected one.
    setSelectedQuestionIndex((s) => {
      if (s === null) return s;
      if (s === source.index) return destination.index;
      if (source.index < s && destination.index >= s) return s - 1;
      if (source.index > s && destination.index <= s) return s + 1;
      return s;
    });
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Selected-question mutations

  const updateSelectedQuestion = (updater) => {
    if (selectedQuestionIndex === null) return;
    updateQuestionAt(selectedQuestionIndex, updater);
  };

  const handleQuestionTextChange = (e) => {
    const value = e.target.value;
    updateSelectedQuestion((q) => ({
      ...q,
      question_v2: [value, q?.question_v2?.[1] || null, q?.question_v2?.[2] || null],
    }));
  };

  const handleQuestionImageUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const fileUUID = await uploadFile(file, "lms_quiz_question_main", homeCompany);
      updateSelectedQuestion((q) => ({
        ...q,
        question_v2: [q?.question_v2?.[0] || "", fileUUID, null],
      }));
    } catch (err) {
      console.error("Question image upload failed", err);
      toast.error("Image upload failed. Please try again.");
    }
  };

  const handleRemoveQuestionImage = () => {
    updateSelectedQuestion((q) => ({
      ...q,
      question_v2: [q?.question_v2?.[0] || "", null, null],
    }));
  };

  const handleToggleDragAndDrop = (e) => {
    const checked = e.target.checked;
    updateSelectedQuestion((q) => ({ ...q, is_drag_and_drop: checked }));
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Answer-level mutations (within selected question)

  const updateAnswerAt = (answerIndex, updater) => {
    updateSelectedQuestion((q) => {
      const answers = [...(q?.answers || [])];
      const current = answers[answerIndex] || makeDefaultAnswer();
      answers[answerIndex] =
        typeof updater === "function" ? updater(current) : updater;
      return { ...q, answers };
    });
  };

  const handleAnswerTextChange = (answerIndex, value) => {
    updateAnswerAt(answerIndex, ([, isCorrect, fileUUID, fileURL]) => [
      value,
      isCorrect,
      fileUUID,
      fileURL,
    ]);
  };

  const handleToggleAnswerCorrect = (answerIndex) => {
    updateAnswerAt(answerIndex, ([text, isCorrect, fileUUID, fileURL]) => [
      text,
      !isCorrect,
      fileUUID,
      fileURL,
    ]);
  };

  const handleAnswerImageUpload = async (answerIndex, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const fileUUID = await uploadFile(
        file,
        "lms_quiz_question_answer",
        homeCompany,
      );
      updateAnswerAt(answerIndex, ([text, isCorrect]) => [
        text,
        isCorrect,
        fileUUID,
        null,
      ]);
    } catch (err) {
      console.error("Answer image upload failed", err);
      toast.error("Image upload failed. Please try again.");
    }
  };

  const handleRemoveAnswerImage = (answerIndex) => {
    updateAnswerAt(answerIndex, ([text, isCorrect]) => [
      text,
      isCorrect,
      null,
      null,
    ]);
  };

  const handleAddAnswer = () => {
    updateSelectedQuestion((q) => {
      const answers = [...(q?.answers || [])];
      if (answers.length >= MAX_ANSWERS) return q;
      answers.push(makeDefaultAnswer());
      return { ...q, answers };
    });
  };

  const handleRemoveAnswer = (answerIndex) => {
    updateSelectedQuestion((q) => {
      const answers = (q?.answers || []).filter((_, i) => i !== answerIndex);
      return { ...q, answers };
    });
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Image lightbox — ImageThumb resolves URLs on mount, so by the time
  // the user clicks a thumb to open the lightbox we already have the
  // presigned URL in hand. No fetch happens here.

  const showLightboxFor = (url) => {
    if (!url) return;
    setLightboxImage(url);
    setShowLightbox(true);
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Save / Close

  const validateBeforeSave = () => {
    if (!modalDataQuiz?.name?.trim()) {
      toast.error("Give your quiz a name first.");
      return false;
    }
    if (questions.length === 0) {
      toast.error("Add at least one question before saving.");
      return false;
    }
    // Surface the first question that has no correct answer marked, so
    // managers don't ship a quiz that's literally unpassable. The same
    // check pattern as SwipeDeckInputsModal:264.
    const invalidIdx = questions.findIndex(
      (q) => countCorrectAnswers(q) === 0,
    );
    if (invalidIdx >= 0) {
      toast.error(
        `Question ${invalidIdx + 1} has no correct answer marked.`,
      );
      setSelectedQuestionIndex(invalidIdx);
      return false;
    }
    for (const [enabled, value, label, maximum] of [
      [passPercentageEnabled, modalDataQuiz?.pass_percentage, "Passing percentage", 100],
      [retakeLimitEnabled, modalDataQuiz?.retake_limit, "Attempt limit", 32767],
    ]) {
      if (
        enabled &&
        (!/^[0-9]+$/.test(String(value ?? "").trim()) ||
          !Number.isInteger(Number(value)) ||
          Number(value) > maximum)
      ) {
        toast.error(`${label} must be a whole number between 0 and ${maximum}, or turned off.`);
        setShowSettings(true);
        return false;
      }
    }
    if (
      modalDataQuiz?.gate_on_pass === false &&
      (!Number(modalDataQuiz?.pass_percentage) || !Number(modalDataQuiz?.retake_limit))
    ) {
      toast.error("A test-out quiz needs a passing percentage and an attempt limit of at least 1.");
      setShowSettings(true);
      return false;
    }
    return true;
  };

  // Centralizes the "stop the AI stream" call so save/close/unmount paths
  // can each invoke it without duplicating the abort + null-the-ref dance.
  const cancelInflightAi = () => {
    if (aiAbortRef.current) {
      aiAbortRef.current.abort();
      aiAbortRef.current = null;
    }
  };

  // Media-library picker. Target captures WHICH image slot opened the
  // library (question vs answer, plus indices at open time). The
  // library returns a LearningFile; quiz images live in FileUpload, so
  // the pick is copied across stores server-side first.
  //
  // Index stability: MediaLibraryModal AWAITS this handler and blocks
  // closing until it resolves, so the editor underneath can't be
  // touched (no reorder/remove) while the copy is in flight — the
  // captured indices stay valid. libraryTarget is cleared by the
  // modal's onClose, which fires only after this resolves.
  //
  // No try/catch here on purpose: a failed copy must REJECT so the
  // modal's own catch toasts it and keeps the picker open (target
  // intact) for a retry — resolving on failure would close the picker
  // with nothing attached.
  const [libraryTarget, setLibraryTarget] = React.useState(null);

  const handlePickFromLibrary = async ({ uuid }) => {
    const target = libraryTarget;
    if (!target) return;
    const res = await postRightworkAPI(
      API_post_file_upload_from_learning_file,
      {
        company_id: homeCompany,
        learning_file_uuid: uuid,
        type:
          target.kind === "question"
            ? "lms_quiz_question_main"
            : "lms_quiz_question_answer",
      },
    );
    const fileUUID = res?.data?.redux?.payload?.file_upload?.uuid;
    if (!fileUUID) throw new Error("Copy did not return a file");
    if (target.kind === "question") {
      updateQuestionAt(target.questionIndex, (q) => ({
        ...q,
        question_v2: [q?.question_v2?.[0] || "", fileUUID, null],
      }));
    } else {
      updateQuestionAt(target.questionIndex, (q) => {
        const answers = [...(q?.answers || [])];
        // Answer removed while the copy was in flight — drop the pick
        // rather than resurrect an empty answer holding only an image.
        if (target.answerIndex >= answers.length) return q;
        const [text, isCorrect] = answers[target.answerIndex];
        answers[target.answerIndex] = [text, isCorrect, fileUUID, null];
        return { ...q, answers };
      });
    }
  };

  // AI-review state: questions stream in flagged _aiPending; the rail
  // shows a Keep/Discard bar until the author decides. Editing or
  // saving counts as keeping (the flag is transient and never
  // persists); per-question discard is the row's existing remove.
  const pendingAiCount = (
    modalDataQuiz?.learning_questions || []
  ).filter((q) => q && q._aiPending).length;

  const handleKeepAiQuestions = () => {
    setModalDataQuiz((prev) => ({
      ...prev,
      learning_questions: (prev.learning_questions || []).map((q) => {
        if (!q || !q._aiPending) return q;
        const { _aiPending, ...rest } = q;
        return rest;
      }),
    }));
  };

  const handleDiscardAiQuestions = () => {
    setModalDataQuiz((prev) => ({
      ...prev,
      learning_questions: (prev.learning_questions || []).filter(
        (q) => !(q && q._aiPending),
      ),
    }));
    // Selection may point at a removed row — reset rather than risk
    // the editor pane showing a different question than the rail.
    setSelectedQuestionIndex(null);
    toast.info("AI questions discarded.", { autoClose: 2500 });
  };

  const handleSave = async () => {
    // Defense-in-depth: the Save button is disabled while loading or after a
    // failed load, but a stale modalDataQuiz post would mean updating the wrong
    // quiz (or worse, creating a brand-new one with uuid: null). Hard-bail here
    // if the parent's fetch hasn't settled or failed.
    if (isLoading || loadError) return;
    if (!validateBeforeSave()) return;
    // Save commits the questions we already have; any cards still in
    // flight from the LLM would arrive after we POST, mutate the parent
    // state post-close, and bleed into the next quiz the user opens.
    cancelInflightAi();
    setAiGenStatus(null);
    setIsSaving(true);
    try {
      const savedQuiz = await post_learning_quiz();
      if (!savedQuiz) return;
      if (savedQuiz && onSaveSuccess) onSaveSuccess(savedQuiz);
      // Persist the "make directly assignable" setting on Save (only when it
      // changed) — like every other quiz setting.
      const desiredAssignable = !!modalDataQuiz?.is_directly_assignable;
      const originalAssignable = !!originalQuizRef.current?.is_directly_assignable;
      if (savedQuiz?.uuid && desiredAssignable !== originalAssignable) {
        await setDirectAssignability({
          companyId: homeCompany,
          assetType: "quiz",
          assetUuid: savedQuiz.uuid,
          isAssignable: desiredAssignable,
        });
      }
      parentHandleClose();
    } finally {
      setIsSaving(false);
    }
  };

  // Work that would be lost by closing: the quiz differs from the snapshot
  // taken when the editor opened (re-taken when the real payload lands for an
  // existing quiz, see the open effect). For a new quiz that means anything
  // was typed or added.
  const quizWouldLoseWork = () => {
    if (isLoading || loadError) return false;
    try {
      return JSON.stringify(modalDataQuiz) !== JSON.stringify(originalQuizRef.current);
    } catch (e) {
      return false;
    }
  };

  const requestClose = () => {
    if (isSaving) return;
    if (quizWouldLoseWork()) setConfirmDiscard(true);
    else handleClose();
  };

  const handleClose = () => {
    setConfirmDiscard(false);
    // Abort any in-flight AI generation before reverting state. Without
    // this, late SSE events would re-append questions onto the freshly
    // restored modalDataQuiz, leaking generated questions across closes.
    cancelInflightAi();
    if (originalQuizRef.current) {
      setModalDataQuiz(originalQuizRef.current);
    }
    setAiGenStatus(null);
    setSelectedQuestionIndex(null);
    setAddMenuAnchor(null);
    parentHandleClose();
  };

  // Safety net: if the component unmounts while a stream is still
  // running (parent route changes, hot reload, etc.), the explicit
  // abort in handleClose/handleSave won't have fired. This catches
  // those paths so we don't leak fetch-readers or queue late state
  // mutations against an unmounted tree.
  useEffect(() => {
    return () => {
      if (aiAbortRef.current) {
        aiAbortRef.current.abort();
        aiAbortRef.current = null;
      }
    };
  }, []);

  const handleOpenPreviewMenu = (e) => {
    if (!questions.length) return;
    setPreviewMenuAnchor(e.currentTarget);
  };

  const handlePreviewIn = (mode) => {
    setPreviewViewport(mode);
    setPreviewMenuAnchor(null);
    setOpenPreview(true);
  };

  // - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // Render

  return (
    <>
      {/* No `onClose` — backdrop click + Escape must not dismiss an editor
          with unsaved work; only the explicit Close (X) closes it. Mirrors
          the Section Inputs modal convention. */}
      <Modal open={open} disableAutoFocus>
        <Box sx={modalStyle}>
          {showLightbox && (
            <div className="absolute w-screen h-screen z-40">
              <Lightbox
                small={lightboxImage}
                medium={lightboxImage}
                hideDownload
                hideZoom
                onClose={() => setShowLightbox(false)}
              />
            </div>
          )}

          {/* = = = Header = = = */}
          <div className="flex items-center gap-3 border-b border-slate-200 bg-white px-4 py-3 shrink-0">
            <div className="w-9 h-9 bg-violet-100 rounded-lg flex items-center justify-center shrink-0">
              <QuizIcon className="text-violet-600" style={{ fontSize: 20 }} />
            </div>
            <TextField
              placeholder={isEdit ? "Quiz name" : "Untitled quiz"}
              value={modalDataQuiz?.name || ""}
              onChange={handleNameChange}
              variant="standard"
              fullWidth
              InputProps={{
                disableUnderline: true,
                sx: {
                  fontSize: "1.25rem",
                  fontWeight: 600,
                  color: "#0f172a",
                },
              }}
            />
            {!isLoading && !loadError && (
              <ReuseSignalBanner count={modalDataQuiz?.section_usage_count} />
            )}

            <button
              type="button"
              onClick={handleOpenPreviewMenu}
              disabled={questions.length === 0 || isLoading || loadError}
              className={[
                "inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors shrink-0",
                questions.length > 0 && !isLoading && !loadError
                  ? "bg-slate-100 hover:bg-slate-200 text-slate-700"
                  : "bg-slate-50 text-slate-300 cursor-not-allowed",
              ].join(" ")}
            >
              <VisibilityIcon style={{ fontSize: 18 }} />
              Preview
              <ArrowDropDownIcon style={{ fontSize: 18, marginLeft: -2 }} />
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving || isLoading || loadError}
              className="inline-flex items-center rounded-lg px-4 py-2 text-sm font-semibold text-white bg-violet-700 hover:bg-violet-800 transition-colors disabled:opacity-60 shrink-0"
            >
              {isSaving ? "Saving…" : "Save Quiz"}
            </button>
            <button
              onClick={requestClose}
              aria-label="Close"
              className="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500 hover:text-slate-700 transition-colors shrink-0"
            >
              <CloseIcon />
            </button>
          </div>


          {/* = = = Body: 3 panes (or loading / failed-load state) = = = */}
          <div className="flex flex-1 overflow-hidden min-h-0">
            {loadError ? (
              <div className="flex-1 flex flex-col items-center justify-center bg-white px-6 text-center">
                <p className="text-slate-900 font-semibold">
                  Couldn't load this quiz
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  It may have been deleted, or there was a network problem. Close
                  and try again.
                </p>
              </div>
            ) : isLoading ? (
              // Parent's fetchQuizDetail is in flight. modalDataQuiz
              // can still hold a previous quiz (or the default blank
              // object) here, so rendering the editor would let users
              // interact with stale data and a quick Save click could
              // post the wrong learning_quiz_uuid (or null). Block the
              // panes entirely until the load resolves; Save + Preview
              // are also gated above.
              <div className="flex-1 flex items-center justify-center bg-white">
                <LoadSpinner />
              </div>
            ) : (
              <>
            {/* - - - Left rail: question list - - - */}
            <QuestionListPane
              pendingAiCount={pendingAiCount}
              onKeepAi={handleKeepAiQuestions}
              onDiscardAi={handleDiscardAiQuestions}
              questions={questions}
              selectedIndex={selectedQuestionIndex}
              hoveredIndex={hoveredQuestionIndex}
              onHover={setHoveredQuestionIndex}
              onSelect={setSelectedQuestionIndex}
              onRemove={handleRemoveQuestion}
              onDragEnd={handleQuestionDragEnd}
              onOpenAddMenu={(anchor) => setAddMenuAnchor(anchor)}
              aiGenStatus={aiGenStatus}
            />

            {/* - - - Center: question editor - - - */}
            <div className="flex-1 overflow-y-auto bg-slate-50">
              {selectedQuestion ? (
                <QuestionEditorPane
                  question={selectedQuestion}
                  questionIndex={selectedQuestionIndex}
                  onQuestionTextChange={handleQuestionTextChange}
                  onQuestionImageUpload={handleQuestionImageUpload}
                  onRemoveQuestionImage={handleRemoveQuestionImage}
                  onToggleDragAndDrop={handleToggleDragAndDrop}
                  onAnswerTextChange={handleAnswerTextChange}
                  onToggleAnswerCorrect={handleToggleAnswerCorrect}
                  onAnswerImageUpload={handleAnswerImageUpload}
                  onOpenLibrary={(target) =>
                    setLibraryTarget({ ...target, questionIndex: selectedQuestionIndex })
                  }
                  onRemoveAnswerImage={handleRemoveAnswerImage}
                  onAddAnswer={handleAddAnswer}
                  onRemoveAnswer={handleRemoveAnswer}
                  onOpenLightbox={showLightboxFor}
                />
              ) : (
                <EmptyEditorPane
                  hasQuestions={questions.length > 0}
                  onAdd={(e) => setAddMenuAnchor(e.currentTarget)}
                  onGenerateAi={() => setOpenAiModal(true)}
                  aiGenStatus={aiGenStatus}
                />
              )}
            </div>

            {/* - - - Right rail: quiz settings - - - */}
            {showSettings ? (
              <QuizSettingsPane
                quiz={modalDataQuiz}
                isEdit={isEdit}
                distinctCategories={distinctCategories}
                onCategoryChange={handleCategoryChange}
                passPercentageEnabled={passPercentageEnabled}
                onPassPercentageToggle={handlePassPercentageToggle}
                onPassPercentageChange={(val) =>
                  updateQuiz({ pass_percentage: val })
                }
                retakeLimitEnabled={retakeLimitEnabled}
                onRetakeLimitToggle={handleRetakeLimitToggle}
                onRetakeLimitChange={(val) => updateQuiz({ retake_limit: val })}
                onShowCorrectAnswersChange={handleShowCorrectAnswersChange}
                onShuffleQuestionsChange={(e) =>
                  updateQuiz({ shuffle_questions: e.target.checked })
                }
                onTestOutChange={handleTestOutChange}
                onSaveToProfileChange={(e) =>
                  updateQuiz({ save_to_profile: e.target.checked })
                }
                assignableValue={modalDataQuiz?.is_directly_assignable}
                onAssignableChange={(val) =>
                  updateQuiz({ is_directly_assignable: val })
                }
                onClose={() => setShowSettings(false)}
                onDeleteClick={() => setOpenDeleteWarning(true)}
                deleteDisabled={isSaving || isLoading || loadError}
              />
            ) : (
              <div className="border-l border-slate-200 bg-white flex items-start py-3 px-1">
                <Tooltip title="Show settings" placement="left">
                  <IconButton
                    size="small"
                    onClick={() => setShowSettings(true)}
                  >
                    <TuneIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </div>
            )}
              </>
            )}
          </div>

          {confirmDiscard && (
            <DiscardChangesConfirm
              noun="quiz"
              isNew={!isEdit}
              onKeepEditing={() => setConfirmDiscard(false)}
              onDiscard={handleClose}
            />
          )}
        </Box>
      </Modal>

      {/* = = = Add-question menu = = = */}
      <Menu
        anchorEl={addMenuAnchor}
        open={Boolean(addMenuAnchor)}
        onClose={() => setAddMenuAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MenuItem onClick={handleAddQuestion}>
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="New question" />
        </MenuItem>
        <MenuItem onClick={handleOpenAiModal}>
          <ListItemIcon>
            <AutoAwesomeIcon fontSize="small" sx={{ color: "#6d28d9" }} />
          </ListItemIcon>
          <ListItemText primary="Generate with AI" />
        </MenuItem>
      </Menu>

      {/* = = = AI generator (existing modal, triggered from add menu) = = = */}
      <MediaLibraryModal
        open={Boolean(libraryTarget)}
        onClose={() => setLibraryTarget(null)}
        companyId={homeCompany}
        onSelect={handlePickFromLibrary}
      />

      <AiQuizGeneratorModal
        open={openAiModal}
        handleClose={() => setOpenAiModal(false)}
        homeCompany={homeCompany}
        sectionsData={sectionsData}
        modulesData={modulesData}
        onGenerate={handleAiGenerate}
      />

      {/* = = = Preview viewport menu = = = */}
      <Menu
        anchorEl={previewMenuAnchor}
        open={Boolean(previewMenuAnchor)}
        onClose={() => setPreviewMenuAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={() => handlePreviewIn("desktop")}>
          <ListItemIcon>
            <DesktopWindowsOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Desktop"
            secondary="Browser-sized preview"
          />
        </MenuItem>
        <MenuItem onClick={() => handlePreviewIn("mobile")}>
          <ListItemIcon>
            <SmartphoneOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Mobile"
            secondary="Phone-sized preview"
          />
        </MenuItem>
      </Menu>

      {/* = = = Preview = = = */}
      <CompleteQuizModal
        open={openPreview}
        handleClose={() => setOpenPreview(false)}
        isLoading={false}
        modalDataQuiz={modalDataQuiz}
        type="Preview"
        mobilePreview={previewViewport === "mobile"}
      />

      {/* = = = Delete = = = */}
      {/* handleCloseInputsModal routes through the LOCAL handleClose,
          not parentHandleClose — delete needs the same abort + status
          reset path as every other exit, otherwise an in-flight AI
          stream keeps running and late onQuestion callbacks leak
          generated questions into the next quiz the user opens.
          (handleClose's originalQuizRef revert is a no-op here since
          the quiz no longer exists post-delete, but the abort matters.) */}
      <DeleteWarningModal
        open={openDeleteWarning}
        handleClose={() => setOpenDeleteWarning(false)}
        type="quiz"
        delete_function={delete_learning_quiz}
        handleCloseInputsModal={handleClose}
      />
    </>
  );
}

// = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = =
// Left rail: question list

function QuestionListPane({
  questions,
  selectedIndex,
  hoveredIndex,
  onHover,
  onSelect,
  onRemove,
  onDragEnd,
  onOpenAddMenu,
  aiGenStatus,
  pendingAiCount,
  onKeepAi,
  onDiscardAi,
}) {
  return (
    <div className="w-[300px] shrink-0 border-r border-slate-200 bg-white flex flex-col overflow-hidden">
      <div className="h-14 px-4 border-b border-slate-200 flex items-center justify-between shrink-0 gap-2">
        <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
          Questions ({questions.length})
        </p>
        {aiGenStatus && (
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-violet-600 text-white text-xs font-semibold shadow-sm">
            <AutoAwesomeIcon
              style={{ fontSize: 14 }}
              className="animate-pulse"
            />
            Generating {aiGenStatus.current}/{aiGenStatus.total}
          </span>
        )}
      </div>

      {/* AI-review bar: generated questions land flagged; nothing is
          final until the author keeps (or edits/saves) them. Hidden
          while the stream is still running — the chip above covers
          that state. */}
      {pendingAiCount > 0 && !aiGenStatus && (
        <div className="px-3 py-2 border-b border-violet-200 bg-violet-50 flex items-center justify-between gap-2 shrink-0">
          <span className="text-xs text-violet-900 font-medium">
            {pendingAiCount} AI question{pendingAiCount === 1 ? "" : "s"} to review
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={onDiscardAi}
              className="text-xs px-2 py-1 rounded text-slate-600 hover:bg-slate-200/70 font-medium"
            >
              Discard
            </button>
            <button
              onClick={onKeepAi}
              className="text-xs px-2 py-1 rounded bg-violet-700 text-white hover:bg-violet-800 font-medium"
            >
              Keep all
            </button>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        {questions.length === 0 && !aiGenStatus ? (
          // Just a list placeholder: the real empty-state CTAs (Add a
          // question / Generate with AI) live in the center EmptyEditorPane,
          // which is on screen at the same time.
          <div className="px-4 py-12 text-center">
            <p className="text-xs text-slate-500">
              No questions yet. Add one below.
            </p>
          </div>
        ) : (
          <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="quiz-questions">
              {(provided) => (
                <div
                  {...provided.droppableProps}
                  ref={provided.innerRef}
                  className="py-1"
                >
                  {questions.map((question, index) => {
                    const draggableId =
                      question.uuid || `q-${index}-${getQuestionTitle(question, index).slice(0, 8)}`;
                    return (
                      <Draggable
                        key={draggableId}
                        draggableId={draggableId}
                        index={index}
                      >
                        {(dragProvided, snapshot) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            style={{
                              ...dragProvided.draggableProps.style,
                              left: "auto !important",
                              top: "auto !important",
                            }}
                          >
                            <QuestionRailRow
                              question={question}
                              index={index}
                              isSelected={index === selectedIndex}
                              isDragging={snapshot.isDragging}
                              isHovered={index === hoveredIndex}
                              onMouseEnter={() => onHover(index)}
                              onMouseLeave={() => onHover(null)}
                              onSelect={() => onSelect(index)}
                              onRemove={() => onRemove(index)}
                              dragHandleProps={dragProvided.dragHandleProps}
                            />
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                  {/* Pending-question skeleton rows — render one per
                      not-yet-arrived question while AI gen is streaming.
                      Sit outside the Droppable's draggable list so they
                      don't interfere with reorder index math. */}
                  {aiGenStatus &&
                    aiGenStatus.total > aiGenStatus.current &&
                    Array.from({
                      length: aiGenStatus.total - aiGenStatus.current,
                    }).map((_, i) => (
                      <PendingQuestionRow
                        key={`pending-${i}`}
                        index={questions.length + i}
                      />
                    ))}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </div>

      <div className="h-14 border-t border-slate-200 px-3 bg-white flex items-center shrink-0">
        <button
          type="button"
          onClick={(e) => onOpenAddMenu(e.currentTarget)}
          className="w-full py-2 px-3 bg-violet-50 hover:bg-violet-100 text-violet-700 text-sm rounded-lg flex items-center justify-center gap-2 font-medium transition-colors"
        >
          <AddIcon fontSize="small" />
          Add Question
        </button>
      </div>
    </div>
  );
}

function QuestionRailRow({
  question,
  index,
  isSelected,
  isDragging,
  isHovered,
  onMouseEnter,
  onMouseLeave,
  onSelect,
  onRemove,
  dragHandleProps,
}) {
  const correctCount = countCorrectAnswers(question);
  const answerCount = (question?.answers || []).length;
  const hasImage = questionHasImage(question);
  const isDragAndDrop = Boolean(question?.is_drag_and_drop);
  const title = getQuestionTitle(question, index);

  return (
    <div
      onClick={onSelect}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={[
        "group flex items-start gap-2 pl-1 pr-2 py-2.5 cursor-pointer transition-colors relative border-b border-slate-100",
        isDragging
          ? "bg-violet-50 shadow-md"
          : isSelected
          ? "bg-violet-50"
          : "bg-white hover:bg-slate-50",
      ].join(" ")}
    >
      {isSelected && (
        <div className="h-full w-1 bg-violet-700 absolute left-0 top-0" />
      )}
      <div
        className="text-slate-400 cursor-grab hover:text-slate-600 mt-0.5"
        {...dragHandleProps}
        onClick={(e) => e.stopPropagation()}
      >
        <DragIndicatorIcon fontSize="small" />
      </div>
      <div className="w-7 h-7 rounded bg-violet-600 flex items-center justify-center shrink-0 mt-0.5">
        <span className="text-white text-xs font-semibold">{index + 1}</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-900 line-clamp-2 leading-snug">
          {title}
        </p>
        <div className="flex items-center gap-1 mt-1 flex-wrap">
          {Boolean(question?._aiPending) && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-violet-100 text-violet-700">
              <AutoAwesomeIcon style={{ fontSize: 10 }} />
              AI
            </span>
          )}
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600">
            {answerCount} {answerCount === 1 ? "choice" : "choices"}
          </span>
          {correctCount === 0 ? (
            <Tooltip title="No correct answer marked">
              <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700">
                <WarningAmberIcon style={{ fontSize: 11 }} />
                0 correct
              </span>
            </Tooltip>
          ) : (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700">
              <CheckCircleIcon style={{ fontSize: 11 }} />
              {correctCount} correct
            </span>
          )}
          {isDragAndDrop && (
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium bg-violet-100 text-violet-700">
              <SwapVertIcon style={{ fontSize: 11 }} />
              D&amp;D
            </span>
          )}
          {hasImage && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600">
              <ImageIcon style={{ fontSize: 11 }} />
            </span>
          )}
        </div>
      </div>
      <Tooltip title="Delete question">
        <span
          className={[
            "transition-opacity",
            isHovered || isSelected ? "opacity-100" : "opacity-0",
          ].join(" ")}
        >
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
          >
            <CloseIcon style={{ fontSize: 14 }} />
          </IconButton>
        </span>
      </Tooltip>
    </div>
  );
}

// Skeleton row shown for each AI question that hasn't streamed in yet.
// Number badge + shimmering text bars match QuestionRailRow's visual
// rhythm so the rail doesn't visibly resize when a real row replaces a
// pending one — fewer layout jumps as questions populate.
function PendingQuestionRow({ index }) {
  return (
    <div
      className="flex items-start gap-2 pl-1 pr-2 py-2.5 border-b border-slate-100 bg-violet-50/30"
      aria-hidden="true"
    >
      <div className="w-5" />
      <div className="w-7 h-7 rounded bg-violet-200 flex items-center justify-center shrink-0 mt-0.5">
        <AutoAwesomeIcon
          className="text-violet-700 animate-pulse"
          style={{ fontSize: 14 }}
        />
      </div>
      <div className="flex-1 min-w-0 space-y-1.5 py-0.5">
        <div className="h-2.5 rounded bg-slate-200 animate-pulse w-[85%]" />
        <div className="h-2.5 rounded bg-slate-200 animate-pulse w-[60%]" />
      </div>
    </div>
  );
}

// = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = =
// Center: question editor

function EmptyEditorPane({ hasQuestions, onAdd, onGenerateAi, aiGenStatus }) {
  if (aiGenStatus) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="max-w-md text-center px-6">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-violet-100 flex items-center justify-center">
            <AutoAwesomeIcon
              className="text-violet-700 animate-pulse"
              style={{ fontSize: 36 }}
            />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">
            Generating questions…
          </h3>
          <p className="text-sm text-slate-500 mb-4">
            {aiGenStatus.current === 0
              ? "The AI is reading your selected content. Questions will appear in the list as they're written."
              : `${aiGenStatus.current} of ${aiGenStatus.total} written so far. Click any to start editing.`}
          </p>
          <div className="w-full max-w-xs mx-auto h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-violet-600 transition-[width] duration-300"
              style={{
                width: `${
                  (aiGenStatus.current / Math.max(aiGenStatus.total, 1)) * 100
                }%`,
              }}
            />
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="h-full flex items-center justify-center">
      <div className="max-w-md text-center px-6">
        <div className="text-5xl mb-4">❓</div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          {hasQuestions ? "Select a question" : "Build your first question"}
        </h3>
        <p className="text-sm text-slate-500 mb-6">
          {hasQuestions
            ? "Pick a question on the left to start editing."
            : "Quizzes are usually 3 to 10 questions. Each one tests a single idea."}
        </p>
        {!hasQuestions && (
          <div className="flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={onAdd}
              className="inline-flex items-center gap-2 rounded-lg bg-violet-700 hover:bg-violet-800 text-white px-4 py-2 font-medium transition-colors"
            >
              <AddIcon style={{ fontSize: 20 }} />
              Add a question
            </button>
            {onGenerateAi && (
              <button
                type="button"
                onClick={onGenerateAi}
                className="inline-flex items-center gap-2 rounded-lg border border-violet-200 text-violet-700 hover:bg-violet-50 px-4 py-2 font-medium transition-colors"
              >
                <AutoAwesomeIcon style={{ fontSize: 18 }} />
                Generate with AI
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function QuestionEditorPane({
  question,
  questionIndex,
  onQuestionTextChange,
  onQuestionImageUpload,
  onOpenLibrary,
  onRemoveQuestionImage,
  onToggleDragAndDrop,
  onAnswerTextChange,
  onToggleAnswerCorrect,
  onAnswerImageUpload,
  onRemoveAnswerImage,
  onAddAnswer,
  onRemoveAnswer,
  onOpenLightbox,
}) {
  const answers = question?.answers || [];
  const correctCount = countCorrectAnswers(question);
  const questionFileUuid = question?.question_v2?.[1];
  const questionFileUrl = question?.question_v2?.[2];

  return (
    <div className="max-w-3xl mx-auto px-8 py-8 space-y-6">
      {/* Question header */}
      <div className="flex items-center gap-3">
        <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-violet-100 text-violet-700 text-xs font-semibold">
          Question {questionIndex + 1}
        </span>
        {correctCount === 0 && (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-medium">
            <WarningAmberIcon style={{ fontSize: 14 }} />
            Mark at least one correct answer
          </span>
        )}
      </div>

      {/* Question text + image */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
        <label className="block text-sm font-medium text-slate-700">
          Question text
        </label>
        <div className="flex items-start gap-3">
          <TextField
            placeholder="What do you want to ask?"
            fullWidth
            multiline
            minRows={2}
            variant="outlined"
            value={question?.question_v2?.[0] || ""}
            onChange={onQuestionTextChange}
            InputProps={{
              sx: {
                backgroundColor: "white",
                borderRadius: "8px",
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#6d28d9",
                },
              },
            }}
          />
          <Tooltip title="Attach image">
            <label
              htmlFor={`q-image-upload-${questionIndex}`}
              className="w-10 h-10 mt-0.5 border border-slate-300 rounded-lg hover:bg-slate-50 flex items-center justify-center text-slate-500 hover:text-slate-700 cursor-pointer transition-colors shrink-0"
            >
              <AttachFileIcon style={{ fontSize: 18 }} />
            </label>
          </Tooltip>
          <input
            id={`q-image-upload-${questionIndex}`}
            type="file"
            accept=".jpg, .jpeg, .png"
            className="hidden"
            onChange={onQuestionImageUpload}
          />
          <Tooltip title="Choose from library">
            <button
              type="button"
              onClick={() => onOpenLibrary({ kind: "question" })}
              className="w-10 h-10 mt-0.5 border border-slate-300 rounded-lg hover:bg-slate-50 flex items-center justify-center text-slate-500 hover:text-slate-700 transition-colors shrink-0"
            >
              <CollectionsOutlinedIcon style={{ fontSize: 18 }} />
            </button>
          </Tooltip>
        </div>

        {questionFileUuid && (
          <ImageThumb
            fileUuid={questionFileUuid}
            cachedUrl={questionFileUrl}
            maxHeight={180}
            onClickUrl={onOpenLightbox}
            onRemove={onRemoveQuestionImage}
            alt="Question image"
          />
        )}

        <div className="pt-2 border-t border-slate-100 flex items-center">
          <Checkbox
            checked={Boolean(question?.is_drag_and_drop)}
            onChange={onToggleDragAndDrop}
            size="small"
            sx={{
              color: "#6d28d9",
              "&.Mui-checked": { color: "#6d28d9" },
            }}
          />
          <div className="ml-1">
            <p className="text-sm text-slate-700">
              Make this a drag-and-drop question
            </p>
            <p className="text-xs text-slate-500">
              Learners drag the correct answers from a pool into a target zone.
            </p>
          </div>
        </div>
      </div>

      {/* Answers */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium text-slate-700">
            Answer choices
          </label>
          <span className="text-xs text-slate-500">
            {answers.length} of {MAX_ANSWERS}
          </span>
        </div>

        <div className="space-y-3">
          {answers.map((answer, idx) => (
            <AnswerRow
              key={idx}
              answer={answer}
              answerIndex={idx}
              questionIndex={questionIndex}
              canRemove={answers.length > 2}
              onTextChange={(v) => onAnswerTextChange(idx, v)}
              onToggleCorrect={() => onToggleAnswerCorrect(idx)}
              onImageUpload={(e) => onAnswerImageUpload(idx, e)}
              onOpenLibrary={() => onOpenLibrary({ kind: "answer", answerIndex: idx })}
              onRemoveImage={() => onRemoveAnswerImage(idx)}
              onRemove={() => onRemoveAnswer(idx)}
              onOpenLightbox={onOpenLightbox}
            />
          ))}
        </div>

        {answers.length < MAX_ANSWERS && (
          <button
            type="button"
            onClick={onAddAnswer}
            className="mt-3 w-full py-2 px-3 border border-dashed border-slate-300 hover:border-violet-500 hover:bg-violet-50 text-slate-600 hover:text-violet-700 rounded-lg text-sm flex items-center justify-center gap-2 transition-colors"
          >
            <AddIcon fontSize="small" />
            Add answer
          </button>
        )}
      </div>

    </div>
  );
}

function AnswerRow({
  answer,
  answerIndex,
  questionIndex,
  canRemove,
  onTextChange,
  onToggleCorrect,
  onImageUpload,
  onOpenLibrary,
  onRemoveImage,
  onRemove,
  onOpenLightbox,
}) {
  const [text, isCorrect, fileUUID, fileURL] = answer || makeDefaultAnswer();
  const letter = ANSWER_LETTERS[answerIndex] || `${answerIndex + 1}`;

  return (
    <div
      className={[
        "border rounded-lg p-3 transition-colors",
        isCorrect
          ? "border-emerald-300 bg-emerald-50/50"
          : "border-slate-200 hover:border-slate-300 bg-white",
      ].join(" ")}
    >
      <div className="flex items-start gap-3">
        <div
          className={[
            "w-8 h-8 rounded-lg flex items-center justify-center text-sm font-semibold mt-1 shrink-0",
            isCorrect
              ? "bg-emerald-600 text-white"
              : "bg-slate-100 text-slate-700",
          ].join(" ")}
        >
          {letter}
        </div>

        <div className="flex-1 space-y-2">
          <TextField
            placeholder={`Answer ${letter}`}
            fullWidth
            variant="outlined"
            size="small"
            value={text || ""}
            onChange={(e) => onTextChange(e.target.value)}
            InputProps={{
              sx: {
                backgroundColor: "white",
                borderRadius: "8px",
                height: "42px",
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#6d28d9",
                },
              },
            }}
          />
          {fileUUID && (
            <ImageThumb
              fileUuid={fileUUID}
              cachedUrl={fileURL}
              maxHeight={80}
              onClickUrl={onOpenLightbox}
              onRemove={onRemoveImage}
              alt="Answer image"
            />
          )}
        </div>

        <div className="flex items-center gap-1 mt-1 shrink-0">
          <Tooltip title="Attach image">
            <label
              htmlFor={`a-image-upload-${questionIndex}-${answerIndex}`}
              className="w-8 h-8 border border-slate-300 rounded-lg hover:bg-slate-50 flex items-center justify-center text-slate-500 hover:text-slate-700 cursor-pointer transition-colors"
            >
              <AttachFileIcon style={{ fontSize: 16 }} />
            </label>
          </Tooltip>
          <input
            id={`a-image-upload-${questionIndex}-${answerIndex}`}
            type="file"
            accept=".jpg, .jpeg, .png"
            className="hidden"
            onChange={onImageUpload}
          />
          <Tooltip title="Choose from library">
            <button
              type="button"
              onClick={onOpenLibrary}
              className="w-8 h-8 border border-slate-300 rounded-lg hover:bg-slate-50 flex items-center justify-center text-slate-500 hover:text-slate-700 transition-colors"
            >
              <CollectionsOutlinedIcon style={{ fontSize: 16 }} />
            </button>
          </Tooltip>

          <Tooltip title={isCorrect ? "Mark as incorrect" : "Mark as correct"}>
            <button
              type="button"
              onClick={onToggleCorrect}
              className={[
                "w-8 h-8 rounded-lg flex items-center justify-center transition-colors",
                isCorrect
                  ? "bg-emerald-600 text-white hover:bg-emerald-700"
                  : "border border-slate-300 text-slate-400 hover:bg-emerald-50 hover:border-emerald-300 hover:text-emerald-600",
              ].join(" ")}
            >
              {isCorrect ? (
                <CheckCircleIcon style={{ fontSize: 18 }} />
              ) : (
                <RadioButtonUncheckedIcon style={{ fontSize: 18 }} />
              )}
            </button>
          </Tooltip>

          {canRemove && (
            <Tooltip title="Remove answer">
              <IconButton size="small" onClick={onRemove}>
                <CloseIcon style={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
}

// = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = =
// Image thumbnail — used for both question and answer attachments.
// Resolves the presigned URL on mount (uses cachedUrl if present so we
// skip the round-trip on freshly-loaded quizzes), shows a skeleton
// while resolving, and a broken-image tile if the fetch fails. The
// hover-revealed X reuses the dot-corner pattern from
// SwipeDeckInputsModal's DeckCoverButton.

function ImageThumb({
  fileUuid,
  cachedUrl,
  maxHeight = 96,
  onClickUrl,
  onRemove,
  alt = "Attached image",
}) {
  const [url, setUrl] = useState(cachedUrl || null);
  const [loading, setLoading] = useState(!cachedUrl);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (cachedUrl) {
      setUrl(cachedUrl);
      setLoading(false);
      setError(false);
      return;
    }
    if (!fileUuid) return;
    let cancelled = false;
    setLoading(true);
    setError(false);
    getRightworkAPI(API_get_file_upload, { file_upload_uuid: fileUuid })
      .then((res) => {
        if (cancelled) return;
        const fetchedUrl = res?.data?.redux?.payload?.presigned_url;
        if (fetchedUrl) {
          setUrl(fetchedUrl);
        } else {
          setError(true);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("ImageThumb load failed", err);
        setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fileUuid, cachedUrl]);

  if (error) {
    return (
      <div
        style={{ height: maxHeight }}
        className="rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-between px-3 gap-2"
      >
        <div className="flex items-center gap-2 text-slate-500 text-xs">
          <BrokenImageOutlinedIcon style={{ fontSize: 16 }} />
          Image unavailable
        </div>
        {onRemove && (
          <IconButton size="small" onClick={onRemove} aria-label="Remove image">
            <CloseIcon style={{ fontSize: 14 }} />
          </IconButton>
        )}
      </div>
    );
  }

  if (loading || !url) {
    return (
      <div
        style={{ height: maxHeight }}
        className="rounded-lg bg-slate-100 animate-pulse"
      />
    );
  }

  return (
    <div className="relative inline-block group">
      <button
        type="button"
        onClick={() => onClickUrl && onClickUrl(url)}
        className="block rounded-lg overflow-hidden border border-slate-200 hover:border-slate-300 transition-colors bg-slate-50"
        style={{ maxHeight, maxWidth: "100%" }}
        aria-label="View full size"
      >
        <img
          src={url}
          alt={alt}
          style={{ maxHeight, display: "block", width: "auto" }}
          draggable={false}
        />
      </button>
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label="Remove image"
          className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-slate-800 text-white shadow opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
        >
          <CloseIcon style={{ fontSize: 12 }} />
        </button>
      )}
    </div>
  );
}

// = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = = =
// Right rail: quiz settings

function QuizSettingsPane({
  quiz,
  isEdit,
  distinctCategories,
  onCategoryChange,
  passPercentageEnabled,
  onPassPercentageToggle,
  onPassPercentageChange,
  retakeLimitEnabled,
  onRetakeLimitToggle,
  onRetakeLimitChange,
  onShowCorrectAnswersChange,
  onShuffleQuestionsChange,
  onTestOutChange,
  onSaveToProfileChange,
  assignableValue,
  onAssignableChange,
  onClose,
  onDeleteClick,
  deleteDisabled,
}) {
  const [showCreateCategory, setShowCreateCategory] = useState(
    distinctCategories.length === 0,
  );

  return (
    <div className="w-[340px] shrink-0 border-l border-slate-200 bg-white flex flex-col overflow-hidden">
      <div className="h-14 px-5 border-b border-slate-200 flex items-center justify-between shrink-0">
        <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
          Quiz Settings
        </p>
        <IconButton size="small" onClick={onClose} title="Hide settings">
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
        {/* Category */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-slate-700">
              Category
            </label>
            <button
              type="button"
              className="text-xs text-violet-600 hover:text-violet-700 font-medium"
              onClick={() => {
                if (showCreateCategory && distinctCategories.length > 0) {
                  setShowCreateCategory(false);
                } else {
                  setShowCreateCategory(true);
                  onCategoryChange("");
                }
              }}
            >
              {showCreateCategory
                ? distinctCategories.length > 0
                  ? "Back to options"
                  : ""
                : "Create new"}
            </button>
          </div>
          {showCreateCategory ? (
            <TextField
              placeholder="New category"
              fullWidth
              variant="outlined"
              size="small"
              value={quiz?.category || ""}
              onChange={(e) => onCategoryChange(e.target.value)}
              InputProps={{
                sx: {
                  backgroundColor: "white",
                  borderRadius: "8px",
                  height: "42px",
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#6d28d9",
                  },
                },
              }}
            />
          ) : (
            <CustomCategorySelect
              options={distinctCategories}
              value={quiz?.category || ""}
              onChange={onCategoryChange}
              name="Category"
              placeholder="Select category"
            />
          )}
        </div>

        {/* Test-out */}
        <SettingToggleCard
          checked={quiz?.gate_on_pass === false}
          onChange={(checked) => onTestOutChange({ target: { checked } })}
          title="Test-out quiz"
          description={`Employees can continue if they don't pass. Passing skips the sections you link to this quiz. To link them, edit a section, open Section Settings, and check Make conditional.`}
        />

        {/* Pass percentage */}
        <SettingToggleCard
          checked={passPercentageEnabled}
          onChange={(checked) =>
            onPassPercentageToggle({ target: { checked } })
          }
          title="Require minimum to pass"
        >
          {passPercentageEnabled && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-700">Passing score</span>
                <TextField
                  placeholder="80"
                  variant="outlined"
                  size="small"
                  value={quiz?.pass_percentage ?? ""}
                  onChange={(e) => onPassPercentageChange(e.target.value)}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                    sx: {
                      width: "100px",
                      backgroundColor: "white",
                      borderRadius: "8px",
                      height: "36px",
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#6d28d9",
                      },
                    },
                  }}
                />
              </div>

              {/* Retake limit — nested under pass percentage since it has
                  no meaning without a passing threshold */}
              <div className="flex items-center justify-between">
                <div className="flex items-center flex-1">
                  <Checkbox
                    checked={retakeLimitEnabled}
                    onChange={onRetakeLimitToggle}
                    size="small"
                    sx={{
                      color: "#6d28d9",
                      "&.Mui-checked": { color: "#6d28d9" },
                    }}
                  />
                  <span className="text-sm text-slate-700 ml-1">
                    Limit attempts
                  </span>
                </div>
                {retakeLimitEnabled && (
                  <TextField
                    placeholder="3"
                    variant="outlined"
                    size="small"
                    value={quiz?.retake_limit ?? ""}
                    onChange={(e) => onRetakeLimitChange(e.target.value)}
                    InputProps={{
                      sx: {
                        width: "100px",
                        backgroundColor: "white",
                        borderRadius: "8px",
                        height: "36px",
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6d28d9",
                        },
                      },
                    }}
                  />
                )}
              </div>
            </div>
          )}
        </SettingToggleCard>

        {/* Show correct answers */}
        <SettingToggleCard
          checked={Boolean(quiz?.show_correct_answers)}
          onChange={(checked) =>
            onShowCorrectAnswersChange({ target: { checked } })
          }
          title="Show correct answers"
          description="Reveal the right answer after the learner submits."
        />

        {/* Shuffle questions */}
        <SettingToggleCard
          checked={Boolean(quiz?.shuffle_questions)}
          onChange={(checked) =>
            onShuffleQuestionsChange({ target: { checked } })
          }
          title="Shuffle question order"
          description="Show the questions in a new random order on each attempt."
        />

        {/* Save to employee profile */}
        <SettingToggleCard
          checked={Boolean(quiz?.save_to_profile)}
          onChange={(checked) =>
            onSaveToProfileChange({ target: { checked } })
          }
          title="Keep a copy on the employee's profile"
          description="The quiz result stays on their profile even after the training changes."
        />

        {/* Make directly assignable */}
        <DirectAssignabilityToggle
          value={assignableValue}
          onChange={onAssignableChange}
        />

        {isEdit && (
          <div className="pt-4 border-t border-slate-200">
            <button
              type="button"
              onClick={onDeleteClick}
              disabled={deleteDisabled}
              className="w-full py-2 px-3 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg text-sm flex items-center justify-center gap-2 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-red-50"
            >
              <DeleteIcon fontSize="small" />
              Delete Quiz
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default QuizEditorModal;
